// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module TopPagesTable
 * @package @geenius/analytics/solidjs
 * @description Renders the SolidJS top-pages table used by analytics dashboards
 * to compare the most-visited routes. The component includes loading and empty
 * states so it can be mounted directly in page layouts.
 */

import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Progress,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@geenius/ui/solidjs";
import { type Component, createEffect, For, onMount, Show } from "solid-js";
import { createAnalyticsCopy } from "../lib/analyticsCopy";
import { type AnalyticsErrorStateValue, ErrorState } from "./ErrorState";

function labelProgressBar(node: HTMLDivElement | null, label: string): void {
  node
    ?.querySelector('[role="progressbar"]')
    ?.setAttribute("aria-label", label);
}

const AccessibleProgress: Component<{
  label: string;
  value: number;
  max: number;
  class?: string;
}> = (props) => {
  let root: HTMLDivElement | undefined;

  const applyLabel = (): void => {
    labelProgressBar(root ?? null, props.label);
  };

  onMount(() => {
    applyLabel();
  });

  createEffect(() => {
    props.label;
    props.value;
    props.max;
    queueMicrotask(() => {
      applyLabel();
    });
  });

  return (
    <div ref={root}>
      <Progress class={props.class} value={props.value} max={props.max} />
    </div>
  );
};

/** Summary row rendered by the SolidJS top-pages table. */
export interface TopPageSummary {
  url: string;
  views: number;
  uniqueVisitors?: number;
  averageTimeMs?: number;
  averageTimeSeconds?: number;
  averageTime?: string;
}

function normalizeCount(value: number | undefined): number | undefined {
  return Number.isFinite(value) ? Math.max(0, value ?? 0) : undefined;
}

type AnalyticsCopy = ReturnType<typeof createAnalyticsCopy>;

function formatDuration(page: TopPageSummary, copy: AnalyticsCopy): string {
  if (page.averageTime) return page.averageTime;

  const milliseconds =
    page.averageTimeMs ??
    (page.averageTimeSeconds !== undefined
      ? page.averageTimeSeconds * 1_000
      : undefined);
  if (!Number.isFinite(milliseconds)) return "—";

  return copy.formatDuration(milliseconds ?? 0);
}

/**
 * Props for rendering the SolidJS top-pages table.
 *
 * @property pages Ordered page summary rows with visit and engagement counts.
 * @property isLoading Whether the table should render placeholder rows.
 * @property error Optional error value rendered instead of the table body.
 * @property onRetry Optional recovery callback shown with the error state.
 * @property class Optional class name merged into the table wrapper.
 */
export interface TopPagesTableProps {
  pages: TopPageSummary[];
  isLoading?: boolean;
  error?: AnalyticsErrorStateValue;
  onRetry?: () => void;
  class?: string;
}

/**
 * Renders the SolidJS top-pages table with proportional view bars.
 *
 * @param props Table props containing page rows and optional loading state.
 * @returns A top-pages table element for analytics dashboards.
 */
export const TopPagesTable: Component<TopPagesTableProps> = (props) => {
  const safePages = () =>
    props.pages.map((page) => ({
      ...page,
      uniqueVisitors: normalizeCount(page.uniqueVisitors),
      views: Number.isFinite(page.views) ? Math.max(0, page.views) : 0,
    }));
  const maxViews = () =>
    safePages().length > 0
      ? Math.max(...safePages().map((page) => page.views), 1)
      : 1;
  const copy = createAnalyticsCopy();
  const progressLabel = (page: TopPageSummary): string =>
    copy.t("analytics.topPages.progressLabel", {
      url: page.url,
      views: copy.formatNumber(page.views),
    });

  if (props.error) {
    return (
      <Card class={props.class}>
        <CardHeader class="pb-0">
          <CardTitle class="text-sm" data-analytics-section-title>
            {copy.t("analytics.topPages.title")}
          </CardTitle>
        </CardHeader>
        <CardContent class="pt-4">
          <ErrorState error={props.error} onRetry={props.onRetry} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Show
      when={!props.isLoading}
      fallback={
        <Card class={props.class}>
          <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-0">
            <CardTitle class="text-sm">
              {copy.t("analytics.topPages.title")}
            </CardTitle>
            <span class="text-xs text-muted-foreground">
              {copy.t("analytics.topPages.loading")}
            </span>
          </CardHeader>
          <CardContent class="grid gap-3 pt-4" data-analytics-top-pages-loading>
            <For each={[1, 2, 3, 4, 5]}>
              {() => <Skeleton class="h-4 w-full" />}
            </For>
          </CardContent>
        </Card>
      }
    >
      <Card class={props.class}>
        <CardHeader class="flex flex-row items-center justify-between space-y-0 pb-0">
          <CardTitle class="text-sm" data-analytics-section-title>
            {copy.t("analytics.topPages.title")}
          </CardTitle>
          <span class="text-xs text-muted-foreground">
            {copy.plural("analytics.topPages.routes", safePages().length)}
          </span>
        </CardHeader>
        <CardContent class="pt-4">
          <Show
            when={safePages().length > 0}
            fallback={
              <EmptyState
                bordered={false}
                compact
                title={copy.t("analytics.topPages.empty")}
              />
            }
          >
            <Table class="w-full">
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {copy.t("analytics.topPages.columns.page")}
                  </TableHead>
                  <TableHead>
                    {copy.t("analytics.topPages.columns.share")}
                  </TableHead>
                  <TableHead class="text-right">
                    {copy.t("analytics.topPages.columns.views")}
                  </TableHead>
                  <TableHead class="text-right">
                    {copy.t("analytics.dashboard.uniqueVisitors")}
                  </TableHead>
                  <TableHead class="text-right">
                    {copy.t("analytics.topPages.columns.averageTime")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <For each={safePages()}>
                  {(page) => (
                    <TableRow>
                      <TableCell class="max-w-0">
                        <span
                          class="block truncate text-sm font-medium text-foreground"
                          data-analytics-page-url
                        >
                          {page.url}
                        </span>
                      </TableCell>
                      <TableCell>
                        <AccessibleProgress
                          class="w-full"
                          label={progressLabel(page)}
                          value={page.views}
                          max={maxViews()}
                        />
                      </TableCell>
                      <TableCell class="text-right text-xs font-semibold text-muted-foreground">
                        <span data-analytics-page-views>
                          {copy.formatNumber(page.views)}
                        </span>
                      </TableCell>
                      <TableCell class="text-right text-xs font-semibold text-muted-foreground">
                        <span data-analytics-page-unique-visitors>
                          {page.uniqueVisitors !== undefined
                            ? copy.formatNumber(page.uniqueVisitors)
                            : "—"}
                        </span>
                      </TableCell>
                      <TableCell class="text-right text-xs font-semibold text-muted-foreground">
                        <span data-analytics-page-average-time>
                          {formatDuration(page, copy)}
                        </span>
                      </TableCell>
                    </TableRow>
                  )}
                </For>
              </TableBody>
            </Table>
          </Show>
        </CardContent>
      </Card>
    </Show>
  );
};
